import { useEffect, useState } from "react";
import { Mail, Check, X, FileText, Download, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ChecklistView } from "@/components/monitoring/Checklist";

const STEP = { contenuto: "Cosa inviare", to: "Destinatari", cc: "Copia", conferma: "In attesa di conferma" };
const KIND = { riepilogo: "Riepilogo conversazione", riassunto: "Riassunto in punti", ultima: "Ultima risposta", proposta: "Proposta PDF (allegato)", relazione: "Relazione PDF (allegato)", relazione_testo: "Relazione nel testo", consulenza: "Consulenza PDF (allegato)" };
const STATO = { inviata: ["Email inviata", Check, "text-green-400"], annullata: ["Email annullata", X, "text-neutral-500"] };

function Row({ label, value, id }) {
  return (
    <div className="flex gap-3 border-b border-neutral-800/70 py-1.5 last:border-0">
      <span className="w-16 shrink-0 text-[10px] uppercase tracking-widest text-neutral-500">{label}</span>
      <span className="min-w-0 flex-1 break-words text-xs text-neutral-200" data-testid={`vesta-vmail-${id}`}>{value || "—"}</span>
    </div>
  );
}

const names = (l) => l.map((c) => (c.name ? `${c.name.split(" · ")[0]} <${c.email}>` : c.email)).join(", ");

export function GuidaCard({ g }) {
  return (
    <div className="mt-5 w-full max-w-md rounded-2xl border border-[#C17F59]/40 bg-neutral-950/80 px-4 py-3 text-left" data-testid="vesta-guida-card">
      <div className="mb-1 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#E8B48F]"><Sparkles className="h-3.5 w-3.5" />{g.titolo}</div>
      {g.cosa && <p className="text-xs leading-relaxed text-neutral-300">{g.cosa}</p>}
      {g.nome && <div className="mt-2 rounded-xl border border-[#C17F59]/30 bg-[#C17F59]/5 p-2.5" data-testid="vesta-guida-nome">
        <div className="mb-0.5 text-[10px] font-bold uppercase tracking-widest text-[#C17F59]">Perché “Vesta”</div>
        <p className="text-xs leading-relaxed text-neutral-300">{g.nome}</p>
      </div>}
      <div className="mt-2 text-[10px] uppercase tracking-widest text-neutral-500">Puoi chiedermi…</div>
      <ul className="mt-1 space-y-1" data-testid="vesta-guida-esempi">{g.esempi.map((e) => <li key={e} className="rounded-lg bg-white/5 px-2.5 py-1 text-xs text-neutral-200">«{e}»</li>)}</ul>
    </div>
  );
}

function TestoRelazione({ id }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/vesta/voice/relazioni/${id}`).then((r) => setD(r.data)).catch(() => setD({ errore: true })); }, [id]);
  if (d?.errore) return <p className="mt-2 text-xs text-red-400" data-testid="vesta-relazione-error">Relazione non disponibile.</p>;
  if (!d) return <Loader2 className="mt-2 h-4 w-4 animate-spin text-[#C17F59]" />;
  if (d.tipo === "checklist") return <div className="mt-2 max-h-80 overflow-y-auto rounded-lg bg-white/5 p-3"><ChecklistView c={d} /></div>;
  return <div className="mt-2 max-h-72 overflow-y-auto whitespace-pre-line rounded-lg bg-white/5 p-3 text-xs leading-relaxed text-neutral-200" data-testid="vesta-relazione-testo">{d.testo}</div>;
}

export function RelazioneCard({ rel }) {
  const [busy, setBusy] = useState(false);
  if (rel.formato === "testo") return (
    <div className="mt-5 w-full max-w-md rounded-2xl border border-[#C17F59]/40 bg-neutral-950/80 px-4 py-3 text-left" data-testid="vesta-relazione-card">
      <div className="mb-1 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#E8B48F]"><FileText className="h-3.5 w-3.5" />{rel.tipo === "checklist" ? "Check-list · testuale" : "Relazione · solo testo"}</div>
      <div className="text-sm font-semibold text-white" data-testid="vesta-relazione-title">{rel.titolo}</div>
      <TestoRelazione id={rel.id} />
    </div>
  );
  const open = async () => {
    setBusy(true);
    const w = window.open("", "_blank");
    try { const r = await api.get(`/vesta/voice/relazioni/${rel.id}/pdf`, { responseType: "blob", timeout: 60000 }); w.location.href = URL.createObjectURL(r.data); }
    catch { w?.close(); toast.error("PDF non disponibile"); }
    setBusy(false);
  };
  return (
    <div className="mt-5 w-full max-w-md rounded-2xl border border-[#C17F59]/40 bg-neutral-950/80 px-4 py-3 text-left" data-testid="vesta-relazione-card">
      <div className="mb-1 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#E8B48F]"><FileText className="h-3.5 w-3.5" />{rel.tipo === "checklist" ? "Check-list pronta" : "Relazione pronta"}</div>
      <div className="text-sm font-semibold text-white" data-testid="vesta-relazione-title">{rel.titolo}</div>
      {!!rel.sezioni?.length && <div className="mt-1 text-[11px] text-neutral-400">{rel.sezioni.join(" · ")}{rel.grafici ? ` · ${rel.grafici} grafici` : ""}</div>}
      <button type="button" onClick={open} disabled={busy} data-testid="vesta-relazione-pdf"
        className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#C17F59] px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest text-[#E8B48F] transition-colors hover:bg-[#C17F59]/20 disabled:opacity-50">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}Apri PDF
      </button>
    </div>
  );
}

// Scheda email vocale: si aggiorna a ogni passo, la conferma resta a voce
export function VoiceMailCard({ flow }) {
  const done = STATO[flow.stato];
  const Icon = done ? done[1] : Mail;
  return (
    <div className="mt-5 w-full max-w-md rounded-2xl border border-[#C17F59]/40 bg-neutral-950/80 px-4 py-3 text-left" data-testid="vesta-vmail-card">
      <div className={`mb-1 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest ${done ? done[2] : "text-[#E8B48F]"}`} data-testid="vesta-vmail-status">
        <Icon className="h-3.5 w-3.5" />{done ? done[0] : `Email · ${STEP[flow.step] || ""}`}
      </div>
      <Row label="Invia" id="content" value={flow.contenuto && `${KIND[flow.contenuto]}${flow.grafici ? ` · ${flow.grafici} ${flow.grafici === 1 ? "grafico" : "grafici"}` : ""}`} />
      <Row label="A" id="to" value={names(flow.to)} />
      <Row label="Cc" id="cc" value={names(flow.cc)} />
      <Row label="Oggetto" id="subject" value={flow.subject} />
      {flow.verifica && (
        <div className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="vesta-vmail-verify">
          {flow.verifica.nota && <div className="mb-0.5 text-[11px] text-amber-300/80">{flow.verifica.nota}</div>}
          Verifica indirizzo: <span className="font-mono font-bold tracking-wide">{flow.verifica.email}</span> · è giusto?
        </div>
      )}
      {!!flow.opzioni?.length && (
        <ol className="mt-2 space-y-0.5 text-xs text-neutral-300" data-testid="vesta-vmail-options">
          {flow.opzioni.map((c, i) => <li key={c.email}>{i + 1}. {c.name || c.email} <span className="text-neutral-500">{c.email}</span></li>)}
        </ol>
      )}
    </div>
  );
}
